/**
 * AdminPayments Page
 * 결제 내역 조회
 */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { tokens } from '../../design-system/tokens';
import { PaymentListItem } from '../../components/molecules/PaymentListItem';
import { adminAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';

export const AdminPayments: React.FC = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadPayments();
  }, [filter, page]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      const params: any = { page };
      if (filter) params.status = filter;

      const response: any = await adminAPI.getPaymentsList(params);
      if (response.success) {
        // Convert ISO date strings to Date objects
        const paymentsWithDates = response.data.payments.map((payment: any) => ({
          ...payment,
          createdAt: payment.createdAt ? new Date(payment.createdAt) : null,
          completedAt: payment.completedAt ? new Date(payment.completedAt) : null
        }));

        setPayments(paymentsWithDates);
        setTotalPages(response.data.totalPages);
        setTotalCount(response.data.totalCount);
      }
    } catch (err: any) {
      console.error('Failed to load payments:', err);
      setError('결제 내역을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    setPage(1); // 필터 변경 시 첫 페이지로
  };

  return (
    <>
      <Header>
          <HeaderContent>
            <HeaderLeft>
              <BackButton onClick={() => navigate('/admin')}>
                ← 대시보드로
              </BackButton>
              <Title>결제 관리</Title>
              <Subtitle>
                {totalCount.toLocaleString()}건의 결제 내역
              </Subtitle>
            </HeaderLeft>
            <HeaderRight>
              <DetailButton
                href="https://admin.portone.io"
                target="_blank"
                rel="noopener noreferrer"
              >
                자세히보기 →
              </DetailButton>
            </HeaderRight>
          </HeaderContent>

          <Filters>
            <FilterButton
              $isActive={filter === ''}
              onClick={() => handleFilterChange('')}
            >
              전체
            </FilterButton>
            <FilterButton
              $isActive={filter === 'completed'}
              onClick={() => handleFilterChange('completed')}
            >
              완료
            </FilterButton>
            <FilterButton
              $isActive={filter === 'pending'}
              onClick={() => handleFilterChange('pending')}
            >
              대기중
            </FilterButton>
            <FilterButton
              $isActive={filter === 'failed'}
              onClick={() => handleFilterChange('failed')}
            >
              실패
            </FilterButton>
            <FilterButton
              $isActive={filter === 'refunded'}
              onClick={() => handleFilterChange('refunded')}
            >
              환불
            </FilterButton>
          </Filters>
        </Header>

        <Content>
          {loading && (
            <LoadingMessage>결제 내역을 불러오는 중...</LoadingMessage>
          )}

          {error && (
            <ErrorMessage>{error}</ErrorMessage>
          )}

          {!loading && !error && payments.length === 0 && (
            <EmptyMessage>
              <EmptyIcon>💳</EmptyIcon>
              <EmptyText>결제 내역이 없습니다</EmptyText>
            </EmptyMessage>
          )}

          {!loading && !error && payments.length > 0 && (
            <>
              <PaymentsList>
                {payments.map(payment => (
                  <PaymentListItem
                    key={payment.paymentId}
                    payment={payment}
                  />
                ))}
              </PaymentsList>

              {totalPages > 1 && (
                <Pagination>
                  <PaginationButton
                    disabled={page === 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                  >
                    ← 이전
                  </PaginationButton>

                  <PageInfo>
                    {page} / {totalPages}
                  </PageInfo>

                  <PaginationButton
                    disabled={page === totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  >
                    다음 →
                  </PaginationButton>
                </Pagination>
              )}
            </>
          )}
        </Content>
    </>
  );
};

const Header = styled.div`
  padding: ${tokens.spacing[6]};
  border-bottom: 1px solid ${tokens.colors.neutral[200]};
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${tokens.spacing[4]};
  gap: ${tokens.spacing[4]};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: flex-start;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${tokens.colors.primary[500]};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  cursor: pointer;
  padding: ${tokens.spacing[2]} 0;
  margin-bottom: ${tokens.spacing[3]};
  transition: color ${tokens.animation.duration.fast};

  &:hover {
    color: ${tokens.colors.primary[600]};
  }
`;

const Title = styled.h1`
  font-size: ${tokens.typography.fontSize['3xl']};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.neutral[800]};
  margin: 0 0 ${tokens.spacing[2]} 0;
`;

const Subtitle = styled.p`
  font-size: ${tokens.typography.fontSize.base};
  color: ${tokens.colors.neutral[600]};
  margin: 0;
`;

const DetailButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${tokens.spacing[1]};
  padding: ${tokens.spacing[2]} ${tokens.spacing[4]};
  background: ${tokens.colors.primary[500]};
  color: ${tokens.colors.neutral[0]};
  border: none;
  border-radius: ${tokens.borderRadius.full};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  text-decoration: none;
  cursor: pointer;
  transition: all ${tokens.animation.duration.fast};

  &:hover {
    background: ${tokens.colors.primary[600]};
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const Filters = styled.div`
  display: flex;
  gap: ${tokens.spacing[2]};
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ $isActive: boolean }>`
  padding: ${tokens.spacing[2]} ${tokens.spacing[4]};
  border: 2px solid ${props => props.$isActive ? tokens.colors.primary[500] : tokens.colors.neutral[300]};
  background: ${props => props.$isActive ? tokens.colors.primary[500] : tokens.colors.neutral[0]};
  color: ${props => props.$isActive ? tokens.colors.neutral[0] : tokens.colors.neutral[700]};
  border-radius: ${tokens.borderRadius.full};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${tokens.animation.duration.fast};

  &:hover {
    border-color: ${tokens.colors.primary[500]};
  }
`;

const Content = styled.div`
  padding: ${tokens.spacing[6]};
`;

const PaymentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[4]};
  margin-bottom: ${tokens.spacing[6]};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${tokens.spacing[4]};
  padding: ${tokens.spacing[4]} 0;
`;

const PaginationButton = styled.button`
  padding: ${tokens.spacing[2]} ${tokens.spacing[4]};
  background: ${tokens.colors.neutral[0]};
  border: 1px solid ${tokens.colors.neutral[300]};
  border-radius: ${tokens.borderRadius.md};
  color: ${tokens.colors.neutral[700]};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${tokens.animation.duration.fast};

  &:hover:not(:disabled) {
    border-color: ${tokens.colors.primary[500]};
    color: ${tokens.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[600]};
  font-weight: ${tokens.typography.fontWeight.medium};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${tokens.spacing[12]};
  font-size: ${tokens.typography.fontSize.lg};
  color: ${tokens.colors.neutral[500]};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: ${tokens.spacing[6]};
  background: ${tokens.colors.semantic.error}15;
  color: ${tokens.colors.semantic.error};
  border-radius: ${tokens.borderRadius.md};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${tokens.spacing[12]};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${tokens.spacing[4]};
`;

const EmptyIcon = styled.div`
  font-size: ${tokens.typography.fontSize['6xl']};
`;

const EmptyText = styled.p`
  font-size: ${tokens.typography.fontSize.xl};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[700]};
  margin: 0;
`;

